import { AlertCircle, Check, ClipboardList, Copy, CreditCard, DollarSign, Folder, Gift } from 'lucide-react';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT
import { getPaymentUrl, getPortalUrl } from '../utils/domainUtils';
import ImmersiveBookingConfirmation from '../layouts/immersive/components/ImmersiveBookingConfirmation';
import { useLayoutContext } from '../shared/hooks/useLayoutContext';
import { DateTimeSection } from '../layouts/centered/components/DateTimeSection';
import { LocationSection } from '../layouts/centered/components/LocationSection';
import { PersonalInfoSection } from '../layouts/centered/components/PersonalInfoSection';
import { ProvidersSection } from '../layouts/centered/components/ProviderSection';
import { SectionTitle } from '../layouts/centered/components/SectionTitle';
import { ServicesSection } from '../layouts/centered/components/ServicesSection';

// New Gift Info Component
//@ts-expect-error
const GiftInfoSection = ({ gift, widgetConfiguration, colors = {} }) => {
  const { t } = useTranslation(); // ADD THIS LINE

  if (!gift) return null;

  const defaultColors = {
    cardBackground: "#000000F2",
    cardBorder: "#DDB88919",
    cardShadow: "rgba(221,184,137,0.07)",
    cardShadowSelected: "rgba(221,184,137,0.1)",
    titleText: "#DDB889",
    subtitleText: "#DDB889CC",
    infoText: "#DDB88999",
    iconColor: "#DDB889"
  };

  // Merge default colors with provided colors
  const mergedColors = { ...defaultColors, ...colors };

  // Create style objects from the colors
  const cardStyle = {
    backgroundColor: mergedColors.cardBackground,
    borderColor: mergedColors.cardBorder,
    boxShadow: `0 0 15px ${mergedColors.cardShadow}`
  };

  const iconStyle = {
    color: mergedColors.iconColor
  };

  const textStyle = {
    color: mergedColors.subtitleText
  };

  const { price, giftType, giftCode } = gift;
  const giftTypeText = giftType === '0' ? t('confirmation.fixedAmount') : t('confirmation.percentage'); // CHANGED: 'Fixed Amount' or 'Percentage'
  const giftValueText = giftType === '0' ? `${widgetConfiguration.currency.symbol}${price.toFixed(2)}` : `${price}%`;

  return (
    <div
      className="lp-border lp-rounded-2xl lp-shadow-lg lp-p-6 lp-transition-all lp-duration-700 hover:lp-shadow-[0_0_20px_rgba(221,184,137,0.1)]"
      style={cardStyle}
    >
      <SectionTitle icon={Gift} title={t('confirmation.giftApplied')} colors={mergedColors} /> {/* CHANGED: "Gift Applied" */}
      <div className="lp-grid lp-grid-cols-1 md:lp-grid-cols-2 lp-gap-4">
        <p className="lp-flex lp-items-center">
          <Gift
            size={16}
            className="lp-mr-2"
            style={iconStyle}
          />
          <span style={textStyle}>{t('confirmation.giftCode')}: {giftCode}</span> {/* CHANGED: "Gift Code:" */}
        </p>
        <p className="lp-flex lp-items-center">
          <DollarSign
            size={16}
            className="lp-mr-2"
            style={iconStyle}
          />
          <span style={textStyle}>{giftTypeText}: {giftValueText}</span>
        </p>
      </div>
    </div>
  );
};
//@ts-expect-error
const BookingHeader = ({ reference, colors = {} }) => {
  const { t } = useTranslation(); // ADD THIS LINE

  const defaultColors = {
    titleText: "#DDB889",
    subtitleText: "#DDB889CC",
    infoText: "#DDB88999",
    iconColor: "#DDB889",
    selectionBackground: "#DDB889",
    selectionIcon: "#000000"
  };

  // Merge default colors with provided colors
  const mergedColors = { ...defaultColors, ...colors };

  const titleStyle = {
    color: mergedColors.titleText
  };

  const subtitleStyle = {
    color: mergedColors.subtitleText
  };

  const infoStyle = {
    color: mergedColors.infoText
  };

  const iconBackgroundStyle = {
    backgroundColor: mergedColors.selectionBackground
  };

  const iconStyle = {
    color: mergedColors.selectionIcon
  };

  const copyIconStyle = {
    color: `${mergedColors.subtitleText}50`
  };

  const copyIconHoverStyle = {
    color: `${mergedColors.subtitleText}70`
  };

  const copyBookingId = () => {
    navigator.clipboard.writeText(reference);
  };

  return (
    <div className="lp-space-y-4 lp-mb-8">
      <div className="lp-flex lp-flex-col lp-items-center lp-gap-4">
        {/* Title with matching icon */}
        <div className="lp-flex lp-items-center lp-gap-3">
          <div
            className="lp-rounded-full lp-p-1.5"
            style={iconBackgroundStyle}
          >
            <Check
              className="lp-w-3 lp-h-3"
              style={iconStyle}
            />
          </div>
          <h2
            className="lp-text-2xl lp-font-medium"
            style={titleStyle}
          >
            {t('confirmation.appointmentCreated')} {/* CHANGED: "Appointment Created" */}
          </h2>
        </div>

        {/* Booking ID with Copy Button */}
        <div className="lp-flex lp-items-center lp-gap-2">
          <span
            className="lp-text-sm"
            style={infoStyle}
          >{t('confirmation.booking')}: </span> {/* CHANGED: "Booking:" */}
          <span
            className="lp-font-medium lp-text-sm"
            style={subtitleStyle}
          >{reference}</span>
          <button
            onClick={copyBookingId}
            className="lp-p-1 lp-rounded-full hover:lp-bg-[#DDB889]/5 lp-transition-all lp-duration-300"
            title={t('confirmation.copyBookingId')} /* CHANGED: "Copy Booking ID" */
          >
            <Copy
              className="lp-w-3.5 lp-h-3.5 hover:lp-text-[#DDB889]/70 lp-transition-colors lp-duration-300"
              style={copyIconStyle}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

// New Total Price Section for Confirmation
//@ts-expect-error
const ConfirmationPriceSection = ({ amount, currency, colors = {} }) => {
  const { t } = useTranslation(); // ADD THIS LINE

  const defaultColors = {
    cardBackground: "#000000F2",
    cardBorder: "#DDB88919",
    cardShadow: "rgba(221,184,137,0.07)",
    cardShadowSelected: "rgba(221,184,137,0.1)",
    titleText: "#DDB889",
    subtitleText: "#DDB889CC",
    infoText: "#DDB88999",
    iconColor: "#DDB889"
  };

  // Merge default colors with provided colors
  const mergedColors = { ...defaultColors, ...colors };

  // Create style objects from the colors
  const cardStyle = {
    backgroundColor: mergedColors.cardBackground,
    borderColor: mergedColors.cardBorder,
    boxShadow: `0 0 15px ${mergedColors.cardShadow}`
  };

  const iconStyle = {
    color: mergedColors.iconColor
  };

  const textStyle = {
    color: mergedColors.subtitleText
  };

  const priceStyle = {
    color: mergedColors.titleText
  };

  return (
    <div
      className="lp-backdrop-blur-sm lp-border lp-rounded-2xl lp-p-6 lp-transition-all lp-duration-700 hover:lp-shadow-[0_0_20px_rgba(221,184,137,0.1)]"
      style={cardStyle}
    >
      <SectionTitle icon={currency.symbol} title={t('price.totalPrice')} colors={mergedColors} /> {/* Existing key */}
      <div className="lp-flex lp-items-center lp-justify-between lp-group">
        <p className="lp-flex lp-items-center">
          {currency?.symbol ? (
            <span
              className="lp-mr-3 lp-shrink-0 lp-transition-transform lp-duration-200 group-hover:lp-scale-110"
              style={iconStyle}
            >
              {currency.symbol}
            </span>
          ) : (
            <DollarSign
              size={20}
              className="lp-mr-3 lp-shrink-0 lp-transition-transform lp-duration-200 group-hover:lp-scale-110"
              style={iconStyle}
            />
          )}
          <span
            className="lp-transition-colors lp-duration-200 group-hover:lp-text-[#DDB889]"
            style={textStyle}
          >
            {t('price.total')} {/* Existing key */}
          </span>
        </p>
        <p
          className="lp-text-xl lp-font-medium"
          style={priceStyle}
        >
          {currency?.symbol || '$'}{amount.toFixed(2)}
        </p>
      </div>
    </div>
  );
};

// Payment Actions Component
//@ts-expect-error
const PaymentActions = ({ booking, widgetConfiguration, colors = {} }) => {
  const { t } = useTranslation();

  const defaultColors = {
    buttonBackground: "#DDB889",
    buttonText: "#000000",
    cardBackground: "#000000F2",
    cardBorder: "#DDB88919",
    titleText: "#DDB889",
    iconColor: "#DDB889"
  };

  // Merge default colors with provided colors
  const mergedColors = { ...defaultColors, ...colors };

  const primaryButtonStyle = {
    backgroundColor: mergedColors.buttonBackground,
    color: mergedColors.buttonText
  };

  const secondaryButtonStyle = {
    backgroundColor: mergedColors.cardBackground,
    borderColor: mergedColors.cardBorder,
    color: mergedColors.titleText
  };

  const iconStyle = {
    color: mergedColors.iconColor
  };

  return (
    <div className="lp-mt-8 lp-text-center">
      <div className="lp-flex lp-flex-col md:lp-flex-row lp-gap-4 lp-justify-center">
        {widgetConfiguration.isPaymentActivated && widgetConfiguration.isPaymentMandatory && (
          <button
            onClick={() => {
              const paymentUrl = getPaymentUrl(booking.id);
              window.location.href = paymentUrl;
            }}
            className="lp-py-3 lp-px-6 lp-rounded-xl hover:lp-bg-opacity-90 lp-transition-all lp-duration-300 lp-ease-in-out lp-flex lp-items-center lp-justify-center"
            style={primaryButtonStyle}
          >
            <CreditCard className="lp-mr-2" size={20} />
            {t('confirmation.payNow')} {/* CHANGED: "Pay Now" */}
          </button>
        )}

        <a
          href={getPortalUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className="lp-border lp-py-3 lp-px-6 lp-rounded-xl lp-transition-all lp-duration-200 hover:lp-shadow-[0_0_15px_rgba(221,184,137,0.1)] hover:lp-border-[#DDB889]/20 lp-flex lp-items-center lp-justify-center group"
          style={secondaryButtonStyle}
        >
          <Folder
            className="lp-mr-2 lp-transition-transform lp-duration-200 group-hover:lp-scale-110"
            size={20}
            style={iconStyle}
          />
          {t('confirmation.dashboard')} {/* CHANGED: "Dashboard" */}
        </a>
      </div>
    </div>
  );
};

interface BookingConfirmationResultProps {
  booking: any;
  selectedServices: any[];
  selectedDateTime: Date;
  customerInfo: any;
  widgetConfiguration: any;
  location: any;
  timeZone: string;
  serviceProviderMap: any;
  nodeData?: any;
}

const BookingConfirmationResult: React.FC<BookingConfirmationResultProps> = ({
  booking,
  selectedServices,
  selectedDateTime,
  customerInfo,
  widgetConfiguration,
  location,
  timeZone,
  serviceProviderMap,
  nodeData
}) => {
  const { t } = useTranslation(); // ADD THIS LINE
  const { layoutType } = useLayoutContext();

  if (layoutType === 'immersive') {
    return (
      <ImmersiveBookingConfirmation
        booking={booking}
        selectedServices={selectedServices}
        selectedDateTime={selectedDateTime}
        customerInfo={customerInfo}
        widgetConfiguration={widgetConfiguration}
        location={location}
        timeZone={timeZone}
        serviceProviderMap={serviceProviderMap}
        nodeData={nodeData}
      />
    );
  }

  // Get settings from nodeData if available
  const hasValidSettings = nodeData?.settings && Object.keys(nodeData.settings).length > 0;
  const settings = hasValidSettings ? nodeData.settings : {
    showAllDetails: true,
    allowEdit: false,
    displayStyle: "list",
    enablePayment: true,
    showCoupon: true,
    showDiscount: true,
    showPrice: true,
    showDateTime: true,
    showLocation: true,
    showPersonalInfo: true,
    showProviders: true,
    showServices: true,
  };

  // Get colors from nodeData if available
  const hasValidColors = nodeData?.settings?.colors && Object.keys(nodeData?.settings?.colors).length > 0;
  const colors = hasValidColors ? nodeData.settings.colors : {
    cardBackground: "#000000F2",
    cardBorder: "#DDB88919",
    cardBorderSelected: "#DDB889",
    cardShadow: "#DDB88919",
    cardShadowSelected: "#DDB88926",
    titleText: "#DDB889",
    subtitleText: "#DDB889CC",
    infoText: "#DDB88999",
    iconColor: "#DDB889",
    selectionBackground: "#DDB889",
    selectionIcon: "#000000",
    buttonBackground: "#DDB889",
    buttonText: "#000000",
    sectionDividerGradient: "#DDB88944",
    avatarBackground: "#DDB8891A"
  };

  const mainTextStyle = {
    color: colors.titleText
  };

  const secondaryTextStyle = {
    color: colors.subtitleText
  };

  // Payment Notice style
  const paymentNoticeStyle = {
    backgroundColor: colors.cardBackground,
    borderColor: colors.cardBorder,
    boxShadow: `0 0 15px ${colors.cardShadow}`
  };

  return (
    <div className="lp-w-full lp-mt-6" style={mainTextStyle}>
      <BookingHeader reference={booking.reference} colors={colors} />

      {/* Payment Notice */}
      {widgetConfiguration.isPaymentActivated && widgetConfiguration.isPaymentMandatory && (
        <div
          className="lp-backdrop-blur-sm lp-border lp-rounded-2xl lp-p-6 lp-transition-all lp-duration-700 lp-mb-6 hover:lp-shadow-[0_0_20px_rgba(221,184,137,0.1)]"
          style={paymentNoticeStyle}
        >
          <div className="lp-flex lp-items-center lp-justify-center lp-gap-2">
            <AlertCircle className="lp-text-red-500" size={24} />
            <p style={mainTextStyle}>
              {t('confirmation.completePaymentNotice')} {/* CHANGED: "Please complete the payment to secure your appointment." */}
            </p>
            <AlertCircle className="lp-text-red-500" size={24} />
          </div>
        </div>
      )}

      {booking.isDepositRequired && (
        <div
          className="lp-backdrop-blur-sm lp-border lp-rounded-2xl lp-p-6 lp-transition-all lp-duration-700 lp-mb-6 hover:lp-shadow-[0_0_20px_rgba(221,184,137,0.1)]"
          style={paymentNoticeStyle}
        >
          <div className="lp-flex lp-items-center lp-justify-center lp-gap-2">
            <AlertCircle className="lp-text-red-500" size={24} />
            <p style={mainTextStyle}>
              {t('confirmation.depositRequired')} {/* CHANGED: "Please complete the payment to secure your appointment." */}
            </p>
            <AlertCircle className="lp-text-red-500" size={24} />
          </div>
        </div>
      )}



      <div className="lp-space-y-6">
        {/* Services and DateTime Section */}
        <div className="lp-grid lp-grid-cols-1 md:lp-grid-cols-2 lp-gap-6">
          {/* Services Section - conditional rendering */}
          {(settings.showAllDetails || settings.showServices) && (
            <ServicesSection
              services={selectedServices}
              currency={widgetConfiguration.currency}
              Icon={ClipboardList}  // Override icon for confirmation view
              colors={colors}
            />
          )}

          {/* DateTime Section - conditional rendering */}
          {(settings.showAllDetails || settings.showDateTime) && (
            <DateTimeSection
              selectedDateTime={selectedDateTime}
              timeZone={timeZone}
              selectedTime={moment(selectedDateTime).format('hh:mm')}
              colors={colors}
            />
          )}
        </div>

        {/* Providers Section - conditional rendering */}
        {(settings.showAllDetails || settings.showProviders) &&
         widgetConfiguration.isProvidersActivated &&
         Object.keys(serviceProviderMap).length > 0 && (
          <ProvidersSection
            serviceProviderMap={serviceProviderMap}
            selectedServices={selectedServices}
            colors={colors}
          />
        )}

        {/* Location Section - conditional rendering */}
        {(settings.showAllDetails || settings.showLocation) &&
         widgetConfiguration.isLocationsActivated &&
         location && (
          <LocationSection
            location={location}
            colors={colors}
          />
        )}

        {/* Personal Information Section - conditional rendering */}
        {(settings.showAllDetails || settings.showPersonalInfo) && (
          <PersonalInfoSection
            customerInfo={customerInfo}
            title={t('confirmation.yourInformation')}  // CHANGED: "Your Information"
            colors={colors}
          />
        )}

        {/* Gift Info Section */}
        {booking.gift && (
          <GiftInfoSection
            gift={booking.gift}
            widgetConfiguration={widgetConfiguration}
            colors={colors}
          />
        )}

        {/* Total Price Section - conditional rendering */}
        {(settings.showAllDetails || settings.showPrice) && (
          <ConfirmationPriceSection
            amount={booking.amount}
            currency={widgetConfiguration.currency}
            colors={colors}
          />
        )}
      </div>

      {/* Payment Actions */}
      {settings.enablePayment && (
        <PaymentActions
          booking={booking}
          widgetConfiguration={widgetConfiguration}
          colors={colors}
        />
      )}

      {/* Footer Message */}
      <div className="lp-mt-8 lp-text-center lp-text-opacity-80" style={secondaryTextStyle}>
        <p className="lp-mb-4">{t('confirmation.lookForwardMessage')}</p> {/* CHANGED: "We look forward to seeing you soon!" */}
        <p className="lp-text-sm">
          {t('confirmation.contactForChanges')} {/* CHANGED: "If you need to make any changes to your booking, please contact us." */}
        </p>
      </div>
    </div>
  );
};

export default BookingConfirmationResult;
